import { useEffect, useRef, type RefObject } from 'react';
import type L from 'leaflet';
import { useUIStore } from '../../store/uiStore';
import { getLeaflet } from './leafletHelpers';

/**
 * Draggable pin for the docked observation composer (panel-system.md Phase
 * 3A, ComposerDockPanel.web.tsx): shown only while
 * uiStore.composerDockOpen is true, starting at the map's current center.
 * Its position lives in uiStore.composerPin, which PostComposer reads as the
 * post's location instead of opening the location drill-in — see
 * PostComposer's `dockedMapPin` prop.
 *
 * Mirrors LocationPickerMap.web.tsx's draggable marker, against the main map
 * instead of a mini one, and gated on the dock's open state instead of a
 * sheet's `visible` prop.
 */
export function useComposerPinLayer({
  mapRef,
  isLoaded,
}: {
  mapRef: RefObject<L.Map | null>;
  isLoaded: boolean;
}): void {
  const open = useUIStore((s) => s.composerDockOpen);
  const markerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded || !open) return;
    const L = getLeaflet();
    const center = map.getCenter();
    const marker = L.marker([center.lat, center.lng], {
      draggable: true,
      // Above post/structure markers so it's never obscured while dragging.
      zIndexOffset: 1000,
    });
    marker.on('dragend', () => {
      const pos = marker.getLatLng();
      useUIStore.getState().setComposerPin({ latitude: pos.lat, longitude: pos.lng });
    });
    marker.addTo(map);
    markerRef.current = marker;
    useUIStore.getState().setComposerPin({ latitude: center.lat, longitude: center.lng });

    return () => {
      marker.remove();
      markerRef.current = null;
    };
  }, [open, isLoaded, mapRef]);
}
